import React, { useEffect, useState, useMemo, useCallback, useRef  } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AssetPreviewCard from './AssetPreviewCard';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import { useTranslation } from 'react-i18next'; 
import { exportToExcel } from './exportUtils';
import { 
  Plus, Search, ChevronLeft, ChevronRight, ArrowUpDown, 
  RefreshCw, Filter, X, Briefcase, Calendar, User, Box,
  CheckSquare, Square, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';

const WorkOrderList = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showAllSites, setShowAllSites] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [view, setView] = useState("");
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [filters, setFilters] = useState({
    wonum: "", description: "", status: "", wo_type: "", assetnum: "", supervisor: "", location_id: "", targstartdate: "", site_id: ""
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'wonum', direction: 'desc' });
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [searchParams, setSearchParams] = useSearchParams();
  
  // 🎯 استخراج الـ view السريع من الرابط (عشان القائمة الجانبية)
  const activeView = searchParams.get('view') || '';

  const [hoveredAsset, setHoveredAsset] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const previewTimerRef = useRef(null);
  
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const token = localStorage.getItem('token');
  const { canCreate, canUpdate, canDelete } = usePermissions('workorders');

  // 🎯 دالة تنفيض الشاشة بالكامل
  const handleClearFilters = () => {
    setSearchParams({}); // بتمسح كل الفلاتر من الرابط (URL)
    setSearchTerm("");   // بتفضي مربع البحث السريع
    setSelectedIds([]);  // بتلغي تحديد أي صفوف (Checkboxes)
  };

  const fetchWorkOrders = useCallback(async (pageNum = 1, isAll = showAllSites, isNewSearch = false) => {
    setLoading(true);
    try {
      const { site_id: filterSiteValue, ...otherFilters } = filters;

      const res = await axios.get('/api/workorders', {
        params: {
          page: pageNum,
          limit: 50,
          all_sites: isAll,
          site_id: sessionSite,
          org_id: sessionOrg,
          searchTerm: searchTerm,
          filter_site: filterSiteValue,
          view: activeView, // 🎯 تمرير فلتر القائمة الجانبية للباك إند
          ...otherFilters
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const newData = res.data.data || [];
      setWorkOrders(prev => isNewSearch ? newData : [...prev, ...newData]);
      setTotalRecords(res.data.total || 0);
      setHasMore(res.data.has_more || false);
      setPage(pageNum);
      if(isNewSearch) setSelectedIds([]);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error(t('wo.messages.sessionExpired'));
      } else {
        toast.error(t('wo.messages.fetchError'));
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionSite, sessionOrg, showAllSites, searchTerm, filters, activeView, token, t]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loading) {
      fetchWorkOrders(page + 1);
    }
  };

  const jumpToPage = (targetPage) => {
    const totalP = Math.ceil(totalRecords / 50) || 1;
    if (targetPage < 1 || targetPage > totalP) return;
    fetchWorkOrders(targetPage, showAllSites, true);
    const tableContainer = document.getElementById('workorder-table-container');
    if (tableContainer) tableContainer.scrollTop = 0;
  };

  // 🎯 إضافة activeView عشان يرفرش لما تدوس على الـ Sidebar
  useEffect(() => {
    fetchWorkOrders(1, showAllSites, true);
  }, [searchTerm, JSON.stringify(filters), showAllSites, activeView]);

  useEffect(() => {
    const openAllSites = () => setShowAllSites(true);
    const viewMySite = () => setShowAllSites(false);
    const wapprView = () => {
        setSearchParams(prev => {
            prev.set('view', 'WAPPR');
            prev.set('page', '1'); // بنصفر الصفحة عشان لو هو في صفحة 5 ومفيش غير 10 نتايج مايطلعش شاشة فاضية
            return prev;
        });
    };

    const inprgView = () => {
        setSearchParams(prev => {
            prev.set('view', 'INPRG');
            prev.set('page', '1');
            return prev;
        });
    };

    const compTodayView = () => {
        setSearchParams(prev => {
            prev.set('view', 'COMP_TODAY');
            prev.set('page', '1');
            return prev;
        });
    };

    const clearView = () => {
        setSearchParams(prev => {
            prev.delete('view'); // لو داس على "كل الأوامر" بنشيل الفلتر خالص
            prev.set('page', '1');
            return prev;
        });
    };
    
    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    window.addEventListener('GLOBAL_WAPPR', wapprView);
    window.addEventListener('GLOBAL_VIEW_INPRG', inprgView);
    window.addEventListener('GLOBAL_VIEW_COMP_TODAY', compTodayView);
    window.addEventListener('GLOBAL_VIEW_ALL_WOS', clearView);
    return () => {
      window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      window.removeEventListener('GLOBAL_VIEW_WAPPR', wapprView);
      window.removeEventListener('GLOBAL_VIEW_INPRG', inprgView);
      window.removeEventListener('GLOBAL_VIEW_COMP_TODAY', compTodayView);
      window.removeEventListener('GLOBAL_VIEW_ALL_WOS', clearView);
    };
  }, [setSearchParams]);

  const toggleSelectOne = (e, wonum) => {
    e.stopPropagation();
    setSelectedIds(prev => 
      prev.includes(wonum) ? prev.filter(id => id !== wonum) : [...prev, wonum]
    );
  };

  const handleRowClick = (wonum) => {
    const activeFilters = {
      ...filters,
      searchTerm,
      view: activeView, // 🎯 تمريره للتفاصيل لو محتاجه
      all_sites: showAllSites 
    };

    const cleanFilters = Object.fromEntries(
      Object.entries(activeFilters).filter(([_, value]) => value !== '' && value !== null)
    );

    const queryParams = new URLSearchParams(cleanFilters).toString();
    navigate(`/workorders/${wonum}${queryParams ? '?' + queryParams : ''}`);
  };

  const filteredWO = useMemo(() => {
    return workOrders.filter(wo => {
        if (!showAllSites) {
          const belongsToUser = wo.site_id === sessionSite && wo.org_id === sessionOrg;
          if (!belongsToUser) return false;
        }
        return true; 
    });
  }, [workOrders, sessionSite, sessionOrg, showAllSites]);

  const sortedData = useMemo(() => {
    let sortableItems = [...workOrders]; 
    
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const valA = String(a[sortConfig.key] || "").toLowerCase();
        const valB = String(b[sortConfig.key] || "").toLowerCase();
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [workOrders, sortConfig]);

  const totalPages = Math.ceil(totalRecords / 50) || 1;

  const getStatusStyle = (status) => {
    switch(status) {
      case 'WAPPR': return 'bg-orange-500 text-white shadow-orange-100';
      case 'APPR': return 'bg-green-600 text-white shadow-green-100';
      case 'COMP': return 'bg-blue-600 text-white shadow-blue-100';
      case 'CLOSE': return 'bg-slate-700 text-white shadow-slate-100';
      case 'INPRG': return 'bg-yellow-500 text-white shadow-yellow-100';
      case 'CANC': return 'bg-red-600 text-white shadow-red-100';
      case 'WMAT': return 'bg-purple-500 text-white shadow-purple-100';
      case 'WSCH': return 'bg-teal-500 text-white shadow-teal-100';
      default: return 'bg-gray-400 text-white';
    }
  };
  const closePreview = useCallback(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    setHoveredAsset(null);
  }, []);

  const handleMouseEnter = async (e, assetnum) => {
    if (!assetnum) return;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    
    setMousePos({ x: e.clientX, y: e.clientY });
    try {
      const res = await axios.get(`/api/getassets/${assetnum}`);
      setHoveredAsset(res.data);
    } catch (err) { console.error("Asset info not found"); }
  };

  const handleExport = useCallback(() => {
    // 🎯 الحل السحري: نستخدم الـ State الداخلي بدل الرابط لأن القيم الحقيقية متخزنة هنا
    const currentSiteFilter = filters.site_id;
    const effectiveSite = !showAllSites && !currentSiteFilter ? sessionSite : currentSiteFilter;

    // تجميع كل الفلاتر النشطة (الحقول + البحث السريع + فيو السايدبار)
    let activeFilters = {
        ...filters,
        searchTerm: searchTerm,
        view: activeView, 
        //org_id: sessionOrg,
    };

    if (effectiveSite) {
        activeFilters.site_id = effectiveSite;
    } else {
        delete activeFilters.site_id; 
    }

    // تنظيف الفلاتر الفاضية عشان متبعتش للباك إند
    activeFilters = Object.fromEntries(
        Object.entries(activeFilters).filter(([_, value]) => value !== '' && value !== null)
    );

    const columnsMap = {
        "wonum": t('reports.designer.fields.wonum', 'WO Num'),
        "description": t('wo.fields.description', 'Description'),
        "assetnum": t('wo.fields.asset', 'Asset'),
        "status": t('wo.headers.status', 'Status'),
        "wo_type": t('wo.fields.workType', 'Type'),
        "site_id": t('wo.fields.site', 'Site'),
        "targstartdate": t('wo.fields.schedStart', 'Start Date')
    };

    exportToExcel('workorders', activeFilters, columnsMap, t);
  }, [filters, searchTerm, activeView, showAllSites, sessionSite, sessionOrg, t]);

  useEffect(() => {
    const exportData = () => { handleExport(); };
    window.addEventListener('GLOBAL_EXPORT', exportData);
    return () => window.removeEventListener('GLOBAL_EXPORT', exportData);
  }, [handleExport]);
  
  useEffect(() => {
    const handleGlobalClick = (e) => {
      closePreview();
    };

    const handleGlobalMouseMove = (e) => {
      if (hoveredAsset) {
        const dist = Math.sqrt(
          Math.pow(e.clientX - mousePos.x, 2) + 
          Math.pow(e.clientY - mousePos.y, 2)
        );
        if (dist > 350) closePreview();
      }
    };

    window.addEventListener('mousedown', handleGlobalClick);
    window.addEventListener('mousemove', handleGlobalMouseMove);
    
    return () => {
      window.removeEventListener('mousedown', handleGlobalClick);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [hoveredAsset, mousePos, closePreview]);

  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] overflow-hidden ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      {hoveredAsset && (
        <div className="fixed pointer-events-none transition-all duration-75"
          style={{ top: mousePos.y + 15, left: mousePos.x + 15, zIndex: 9999 }}>
          <AssetPreviewCard asset={hoveredAsset} />
        </div>
      )}    

      {/* Header Panel */}
      <div className="bg-white p-3 border-b shadow-sm z-10 space-y-3">
        <div className={`flex justify-between items-center`}>
          <div className={`flex items-center gap-6 ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <h2 className={`text-[#005495] font-black italic uppercase text-lg flex items-center gap-2`}>
              <Briefcase size={22} className="text-blue-900"/>
               {t('wo.headers.trackingTitle')}
               <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold border ${showAllSites ? 'bg-red-100 text-red-700 border-red-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
                {showAllSites ? t('wo.headers.globalView') : `${t('wo.headers.site')}: ${sessionSite}`}
              </span>
               <RefreshCw size={20} className={loading ? 'animate-spin' : 'cursor-pointer'} onClick={() => fetchWorkOrders(1, showAllSites, true)} />
            </h2>
            <div className="relative">
              <Search className={`absolute top-2 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} size={14} />
              <input 
                className={`py-1.5 border border-gray-300 w-80 outline-none focus:border-blue-500 shadow-inner rounded-sm font-bold uppercase ${isRTL ? 'pr-9 pl-4 text-right' : 'pl-9 pr-4 text-left'}`} 
                placeholder={t('wo.placeholders.searchWO')} 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {selectedIds.length > 0 && (
              <div className={`flex items-center gap-2 bg-orange-50 px-3 py-1.5 border border-orange-200 rounded-sm animate-in fade-in ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CheckSquare size={14} className="text-orange-600 font-bold"/>
                <span className="text-orange-700 font-black uppercase">{t('wo.headers.selected')}: {selectedIds.length}</span>
                <button onClick={() => setSelectedIds([])} className={`text-gray-400 hover:text-red-500 ${isRTL ? 'mr-2' : 'ml-2'}`}><X size={14}/></button>
              </div>
            )}
            <button 
            onClick={handleClearFilters} 
            className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-red-600 font-bold transition-colors"
            title={t('common.clear_filters')}
          >
            <X size={16} strokeWidth={3} /> {t('common.clear_filters', 'CLEAR FILTERS')}
          </button>
          </div>
          {/* زرار تنظيف الفلاتر */}
          
          {canCreate && (
          <button onClick={() => navigate('/workorders/new')} className={`bg-[#3c70a4] text-white px-6 py-2 rounded-sm font-bold flex items-center gap-2 uppercase hover:bg-[#1a3353] shadow-lg transition-all ${isRTL ? 'ml-4' : 'mr-4'}`}>
            <Plus size={16} /> {t('wo.actions.newWO')}
          </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div id="workorder-table-container" className="flex-1 overflow-auto p-4 custom-scrollbar" onScroll={handleScroll}>
        <div className="bg-white border shadow-sm rounded-sm overflow-hidden">
          <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-[#f9fafb] border-b text-gray-700 font-bold uppercase sticky top-0 z-20">
              <tr className={`border-b ${isRTL ? 'flex-row-reverse' : ''}`}>
                <th className={`p-3 border-x w-10 text-center ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>
                  <button onClick={() => setSelectedIds(selectedIds.length === sortedData.length ? [] : sortedData.map(x => x.wonum))}>
                    {selectedIds.length === sortedData.length && sortedData.length > 0 ? <CheckSquare size={16}/> : <Square size={16}/>}
                  </button>
                </th>
                <th className={`p-3 border-x cursor-pointer hover:bg-gray-50 w-48 ${isRTL ? 'border-l' : 'border-r'}`} onClick={() => setSortConfig({key: 'wonum', direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'})}>
                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>{t('reports.designer.fields.wonum')} <ArrowUpDown size={10}/></div>
                </th>
                <th className={`p-3 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.fields.description')}</th>
                <th className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.fields.location')}</th>
                <th className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.fields.asset')}</th>                                
                <th className={`p-3 border-x w-24 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.headers.status')}</th>
                <th className={`p-3 border-x w-24 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.fields.workType')}</th>
                <th className={`p-3 border-x w-32 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.fields.schedStart')}</th>
                <th className="p-3 w-32 text-center italic opacity-60">{t('wo.fields.site')}</th>
              </tr>
              <tr className={`bg-[#f1f5f9] ${isRTL ? 'flex-row-reverse' : ''}`}>
                <td className="border-x"></td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}><input value={filters.wonum} onChange={e => setFilters({...filters, wonum: e.target.value})} className={`w-full border border-gray-300 p-1 outline-none font-normal uppercase ${isRTL ? 'text-right' : 'text-left'}`} /></td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}><input value={filters.description} onChange={e => setFilters({...filters, description: e.target.value})} className={`w-full border border-gray-300 p-1 outline-none font-normal ${isRTL ? 'text-right' : 'text-left'}`} /></td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}><input value={filters.location_id} onChange={e => setFilters({...filters, location_id: e.target.value})} className="w-full border border-gray-300 p-1 outline-none uppercase text-center" /></td>            
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}><input value={filters.assetnum} onChange={e => setFilters({...filters, assetnum: e.target.value})} className="w-full border border-gray-300 p-1 outline-none uppercase text-center" /></td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
                    <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} className={`w-full border border-gray-300 p-1 outline-none font-bold uppercase text-[9px] ${isRTL ? 'text-right' : 'text-left'}`}>
                        <option value="">{t('assets.list.all')}</option>
                        <option value="WAPPR">WAPPR</option>
                        <option value="APPR">APPR</option>
                        <option value="COMP">COMP</option>
                        <option value="CLOSE">CLOSE</option>
                        <option value="INPRG">INPRG</option>
                        <option value="CANC">CANC</option>
                    </select>
                </td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
                    <select value={filters.wo_type} onChange={e => setFilters({...filters, wo_type: e.target.value})} className={`w-full border border-gray-300 p-1 outline-none font-bold uppercase text-[9px] ${isRTL ? 'text-right' : 'text-left'}`}>
                        <option value="">{t('assets.list.all')}</option>
                        <option value="CM">CM</option>
                        <option value="PM">PM</option>
                        <option value="EM">EM</option>
                    </select>
                </td>
                <td className={`p-1.5 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
                  <DateField value={filters.targstartdate} onChange={val => setFilters({...filters, targstartdate: val})} />
                </td>
                <td className="p-1.5 border-x">
                   <DataField value={filters.site_id} onChange={val => setFilters({...filters, site_id: val})} />
                </td>
              </tr>
            </thead>
            <tbody>
              {loading && workOrders.length === 0 ? (
                <tr><td colSpan="9" className="p-10 text-center animate-pulse text-blue-900 font-bold uppercase tracking-widest">{t('wo.messages.loadingWOs')}</td></tr>
              ) : sortedData.map((wo, i) => (
                <tr key={`${wo.wonum}-${i}`} className={`border-b hover:bg-blue-50 cursor-pointer group transition-all ${selectedIds.includes(wo.wonum) ? 'bg-blue-50' : ''} ${isRTL ? 'flex-row-reverse' : ''}`} onClick={() => handleRowClick(wo.wonum)}>
                  <td className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`} onClick={(e) => toggleSelectOne(e, wo.wonum)}>
                    {selectedIds.includes(wo.wonum) ? <CheckSquare size={16} className="text-blue-700 mx-auto"/> : <Square size={16} className="text-gray-300 mx-auto group-hover:text-blue-300"/>}
                  </td>
                  <td className={`p-3 border-x font-black text-blue-900 uppercase group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>{wo.wonum}</td>
                  <td className={`p-3 border-x text-gray-600 font-semibold ${isRTL ? 'border-l' : 'border-r'}`}>{wo.description}</td>
                  <td className={`p-3 border-x font-bold text-blue-800 text-center uppercase ${isRTL ? 'border-l' : 'border-r'}`}>{wo.location_id || '---'}</td>
                  <td 
                    className={`p-3 border-x font-bold text-blue-800 text-center uppercase ${isRTL ? 'border-l' : 'border-r'}`}
                    onMouseEnter={(e) => handleMouseEnter(e, wo.assetnum)}
                    onMouseLeave={() => setHoveredAsset(null)}
                  >
                    <span className="hover:underline hover:text-blue-600 cursor-help">
                      {wo.assetnum || '---'}
                    </span>
                  </td>                
                  <td className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                    <span className={`px-3 py-1 rounded-sm text-[8px] font-black uppercase shadow-sm ${getStatusStyle(wo.status)}`}>
                      {wo.status}
                    </span>
                  </td>
                  <td className={`p-3 border-x text-center font-bold text-gray-500 ${isRTL ? 'border-l' : 'border-r'}`}>{wo.wo_type}</td>
                  <td className={`p-3 border-x text-center font-bold text-gray-500 ${isRTL ? 'border-l' : 'border-r'}`}>{formatDateTime(wo.targstartdate)}</td>                 
                  <td className="p-3 text-blue-900 font-black text-center italic opacity-60 uppercase">{wo.site_id || '---'}</td>
                </tr>
              ))}
              {loading && workOrders.length > 0 && (
                 <tr><td colSpan="9" className="p-4 text-center font-black text-blue-900 animate-pulse uppercase">{t('wo.messages.syncing')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hybrid Pagination Footer */}
      <div className={`bg-[#1a3353] text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-6 text-[10px] font-black uppercase tracking-widest ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Box size={14} className="text-blue-400"/>
            <span>{t('wo.footer.loaded')}: <span className="text-blue-300 mx-1">{workOrders.length}</span></span>
          </div>
          <div className={`flex items-center gap-2 border-white/20 px-6 ${isRTL ? 'border-r flex-row-reverse' : 'border-l'}`}>
            <Filter size={14} className="text-orange-400"/>
            <span>{t('wo.footer.total')}: <span className="text-blue-300 mx-1">{totalRecords}</span></span>
          </div>
        </div>

        <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button disabled={page === 1 || loading} onClick={() => jumpToPage(1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all">
              <ChevronLeft size={18} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
            </button>
            <button disabled={page === 1 || loading} onClick={() => jumpToPage(page - 1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all">
              <ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
            </button>
            
            <div className={`px-4 text-[11px] font-mono border-x border-white/10 mx-1 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className="text-gray-400 italic text-[9px]">{t('wo.footer.page')}</span>
              <span className="text-orange-400 font-black text-sm">{page}</span>
              <span className="text-gray-400 italic text-[9px]">{t('wo.footer.of')} {totalPages}</span>
            </div>

            <button disabled={!hasMore || loading} onClick={() => jumpToPage(page + 1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all">
              <ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/>
            </button>
            <button disabled={!hasMore || loading} onClick={() => jumpToPage(totalPages)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all">
              <ChevronRight size={18} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
            </button>
          </div>
          <RefreshCw size={16} className={`cursor-pointer ${loading ? 'animate-spin text-orange-400' : 'text-blue-300'}`} onClick={() => fetchWorkOrders(1, showAllSites, true)} />
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return dateString.split('.')[0].substring(0, 16).replace('T', ' ');
};
  
const DateField = ({ value, onChange }) => (
  <div className="w-full">
    <input 
      type="datetime-local" 
      className="w-full border border-gray-300 p-1 outline-none text-[11px] uppercase font-normal bg-white text-center" 
      value={formatDateTime(value)} 
      onChange={e => onChange(e.target.value)} 
    />
  </div>
);

const DataField = ({ value, onChange }) => (
  <div className="w-full">
    <input 
      className="w-full border border-gray-300 p-1 outline-none text-[11px] uppercase font-normal bg-white text-center font-bold" 
      value={value} 
      onChange={e => onChange(e.target.value)} 
    />
  </div>
);

export default WorkOrderList;